import { AxiosError } from 'axios';

import type { Sale } from 'types/Sale';

import SegmentIO from 'reporting/SegmentIO';
import WavefrontReporter from 'reporting/wavefront/WavefrontReporter';
import HttpClient from 'server/helpers/HttpClient';
import { Auth } from 'types/Auth';
import { Config } from 'types/Config';
import { FeatureFlags } from 'types/FeatureFlags';
import { Insight } from 'types/Insight';

const httpClient = HttpClient.getInstance();
const { SplunkReporter } = require('reporting/splunk/SplunkReporter');
const splunkReporter = SplunkReporter.getInstance();
const logger = 'businessLogic/Auth';
const wavefrontReporter = WavefrontReporter.getInstance();

interface signOutParam {
  ssrtid: string;
  portal: string;
  token: string;
}

interface signInParams {
  entityId: string;
  endpoints: { signInURL: string };
}

interface signUpParams {
  entityId: string;
  endpoints: { signUpURL: string };
}

export const signInBL = ({ entityId, endpoints }: signInParams) => {
  splunkReporter.contextual({
    logInfo: { logLevel: 'info', logger },
    event: 'signIn',
    action: 'redirect',
    entityId,
    Refactored: true,
  });
  localStorage.setItem('from_sign_in', 'true');
  SegmentIO.transactionEngaged({
    activity_type: 'sign_in',
    ui_object: 'link',
    ui_action: 'clicked',
    ui_access_point: 'transaction_flow',
    ui_object_detail: 'wallet_sign_in',
  });

  window.location.href = endpoints.signInURL;
};

export const signUpBL = ({ entityId, endpoints }: signUpParams) => {
  splunkReporter.contextual({
    logInfo: { logLevel: 'info', logger },
    event: 'signUp',
    action: 'redirect',
    Refactored: true,
    UserEntityId: entityId,
  });
  localStorage.setItem('from_sign_up', 'true');
  SegmentIO.transactionEngaged({
    activity_type: 'sign_up',
    ui_object: 'link',
    ui_action: 'clicked',
    ui_access_point: 'transaction_flow',
    ui_object_detail: 'wallet_sign_up',
  });

  window.location.href = endpoints.signUpURL;
};

export const signOutBL = async ({ ssrtid, portal, token }: signOutParam) => {
  const endpoint = `/${portal}/rest/authenticate/logout`;
  splunkReporter.contextual({
    logInfo: { logLevel: 'info', logger },
    event: 'signOut',
    action: 'clicked',
    Refactored: 'true',
  });

  const data = { url: endpoint, endpoint, data: { event: 'signOut', ssrtid, token } };
  await httpClient.delete(endpoint, data);

  setTimeout(() => {
    window.location.reload();
  }, 0);
};

export const refreshAuthBL = async (args: {
  portal: string;
  ssrtid: string;
  authToken: string;
  token: string;
}) => {
  const { ssrtid, portal, authToken, token } = args;
  const endpoint = `/${portal}/rest/authenticate/refresh-auth`;
  splunkReporter.contextual({
    logInfo: { logLevel: 'info', logger },
    event: 'refreshAuth',
    action: 'begin',
  });
  const r = await httpClient.get(endpoint, {
    responseType: 'json',
    endpoint,
    headers: {
      Authorization: `Bearer ${authToken}`,
      'ssr-session-id': ssrtid,
      'intuit-acstoken': token,
    },
  });
  return r.data;
};

export const checkIsUsernameFree = async ({
  emailAddress,
  config,
  riskProfileToken,
  locale,
}: {
  emailAddress: string;
  config: Config;
  riskProfileToken?: string;
  locale: string;
}): Promise<boolean> => {
  const startTime = Date.now();
  const query = `query VerifyUsernameAvailable($input: Identity_EmailCredentialAvailableInput!) {
    identityEmailCredentialAvailable(input: $input) {
        __typename
        ... on Identity_CredentialAvailableSuccess { available }
        ... on Identity_CredentialUnauthorizedError { message }
    }
}`;

  const variables = {
    input: {
      emailAddress: emailAddress,
    },
  };

  try {
    if (!riskProfileToken) {
      throw new Error('Risk profile token not provided');
    }

    const endpoint = `${config.endpoints.identityUrl}/v1/digital-identity/graphql`;
    const browserKey = config.credentials.browserApiKey;
    const webClientAlias = config.credentials.webClientAssetAlias;
    SegmentIO.frictionlessExperienceUsernameAvailabilityReqToIdentity();
    const { data: graphQlResponse } = await httpClient.post(
      endpoint,
      { query, variables },
      {
        endpoint,
        headers: {
          accept: '*/*',
          'accept-language': 'en-US,en;q=0.9',
          authorization: `Intuit_APIKey intuit_apikey=${browserKey}, intuit_apikey_version=1.0`,
          'cache-control': 'max-age=0',
          'content-type': 'application/json',
          intuit_authn_session_slot: 'qbn',
          intuit_caller_type: 'REACT_WIDGET',
          intuit_country: locale,
          intuit_identity_routing: 'SBSEG',
          intuit_intentassetalias: webClientAlias,
          intuit_interception: 'ignore-response',
          intuit_locale: locale,
          intuit_offeringenv: config.env,
          intuit_offeringid: webClientAlias,
          intuit_origin: window.location.origin,
          intuit_profilingid: riskProfileToken,
          intuit_samesite_support: 'true',
          intuit_sessionid: riskProfileToken,
        },
      }
    );

    if (
      graphQlResponse?.data?.identityEmailCredentialAvailable?.__typename ===
      'Identity_CredentialAvailableSuccess'
    ) {
      const isAvailable = graphQlResponse.data.identityEmailCredentialAvailable.available;
      const executionTimeMs = Date.now() - startTime;

      // Track successful request using WavefrontReporter (SSR pattern like frictionless)
      try {
        if (!wavefrontReporter.baseEndpoint) {
          return isAvailable;
        }
        await wavefrontReporter.trackUsernameAvailabilityCheck({
          isAvailable,
          executionTimeMs,
          status: 'success',
        });
      } catch (err) {
        // Silently fail if tracking fails
      }

      return isAvailable;
    }

    throw new Error(
      `Did not get expected response, got: ${JSON.stringify(graphQlResponse?.data, null, 2)}`
    );
  } catch (err) {
    const executionTimeMs = Date.now() - startTime;

    // Track failed request using WavefrontReporter (SSR pattern like frictionless)
    try {
      await wavefrontReporter.trackUsernameAvailabilityCheck({
        isAvailable: true,
        executionTimeMs,
        status: 'error',
      });
    } catch (trackingErr) {
      // Silently fail if tracking fails
    }

    // Extract HTTP status code from axios error if available
    const statusCode = (err as AxiosError)?.response?.status;
    const statusText = (err as AxiosError)?.response?.statusText;

    splunkReporter.contextual({
      logInfo: { logLevel: 'error', logger },
      event: 'checkUsernameAvailable',
      action: 'checkUsernameAvailable',
      activityInfo: {
        statusCode,
        statusText,
      },
      error: err,
    });
  }

  return true; // if we get an error, we allow the user to enter their email and try to sign up
};

export const isFrictionlessLoginEligibleBL = ({
  frictionlessLoginEmail,
  frictionlessLoginSessionId,
  featureFlags,
  sale,
  insight,
  isForcedAutopayRecurring = false,
}: {
  frictionlessLoginEmail?: string;
  frictionlessLoginSessionId?: string;
  featureFlags: FeatureFlags;
  sale: Pick<Sale, 'type'>;
  insight: Pick<Insight, 'isPayable'>;
  isForcedAutopayRecurring?: boolean;
}) => {
  if (!sale) {
    return false;
  }
  if (!insight.isPayable) {
    // FrictionlessLogin container is connected only in the PaymentForm on the Payment page, so it is not displayed for non-payable or already paid invoices.
    // But we need this check in the BL for the "Amplitude frictionless eligibility flag" not to be reported for those non-payable invoices.
    return false;
  }
  const isSubscription = sale.type === 'SUBSCRIPTION';
  const featureFlag = isSubscription
    ? 'SBSEG-CP-cp-subsction-frictionless-login-enabled'
    : 'SBSEG-CP-cp-frictionless-login-enabled';

  if (!frictionlessLoginEmail || !frictionlessLoginSessionId) {
    return false;
  }

  if (isForcedAutopayRecurring) {
    return true;
  }

  return !!featureFlags[featureFlag];
};

/**
 * If frictionless login modal should be shown
 */
export const isFrictionlessLoginEnabledBL = async ({
  isUserSignedIn,
  frictionlessLoginEmail,
  frictionlessLoginSessionId,
  featureFlags,
  sale,
  insight,
  isForcedAutopayRecurring = false,
}: {
  isUserSignedIn: boolean;
  frictionlessLoginEmail?: string;
  frictionlessLoginSessionId?: string;
  featureFlags: FeatureFlags;
  sale: Pick<Sale, 'type'>;
  insight: Pick<Insight, 'isPayable'>;
  isForcedAutopayRecurring?: boolean;
}) => {
  if (
    !isFrictionlessLoginEligibleBL({
      frictionlessLoginEmail,
      frictionlessLoginSessionId,
      featureFlags,
      sale,
      insight,
      isForcedAutopayRecurring,
    })
  ) {
    return false;
  }
  if (isUserSignedIn) {
    return false;
  }
  return true;
};

export const isFrictionlessCheckoutEligibleBL = ({
  region,
  featureFlags,
  insight,
  auth,
  sale,
}: {
  region: String;
  featureFlags: FeatureFlags;
  insight: Insight;
  auth: Auth;
  sale: Sale;
}): boolean => {
  // To see the Frictionless Checkout experience, users must be:
  // 1. With the feature flag enabled
  if (!featureFlags?.['SBSEG-CP-cp-frictionless-login-phase-2-enabled']) {
    return false;
  }
  // 2. From US, viewing an invoice and not signed in
  if (region !== 'US' || sale?.type !== 'INVOICE' || auth?.isUserSignedIn) {
    return false;
  }

  // 3. Viewing a payable invoice with either card or bank enabled
  if (
    !insight?.isPayable ||
    !insight?.isPayEnabled ||
    (!insight?.view2pay?.isCardEnabled && !insight?.view2pay?.isBankEnabled)
  ) {
    return false;
  }

  return true;
};

export const shouldRaiseAAL = (error: AxiosError): boolean => {
  try {
    const remediationToken = error?.response?.headers?.['intuit_remediations'];
    if (!remediationToken) {
      return false;
    }

    const parsedRemediationToken: { id: string; type: string }[] = JSON.parse(
      atob(remediationToken)
    );
    const requiredRemediationKey = 'irn:intuit::identity:remediation/session-has-verified-phone';
    const requiredRemediation = parsedRemediationToken.find(
      ({ id }) => id === requiredRemediationKey
    );
    return !!requiredRemediation;
  } catch (error) {
    splunkReporter.contextual({
      logInfo: { logLevel: 'error', logger },
      event: 'parseRemediationToken',
      action: 'frictionlessLogin',
      error,
    });
    return false;
  }
};
